import {
  Add as AddIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
  MoreVert as MoreVertIcon,
} from "@mui/icons-material";
import MultipleStopIcon from "@mui/icons-material/MultipleStop";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Divider,
  IconButton,
  Menu,
  MenuItem,
  Stack,
  Typography,
} from "@mui/material";
import { useQueryClient } from "@tanstack/react-query";
import type React from "react";
import { useState } from "react";
import { Panel, PanelGroup, PanelResizeHandle } from "react-resizable-panels";
import { EmptyState } from "@/components/ui";
import MagicDefinitionDialog from "../components/magic-definition-dialog.tsx";
import MagicGraphVisualization from "../components/magic-graph-visualization.tsx";
import MagicSidePanel from "../components/magic-side-panel.tsx";
import PageHeader from "../components/page-header.tsx";
import {
  useCreateMagicDefinition,
  useDeleteMagicDefinition,
  useMagicDefinitions,
  useUpdateMagicDefinition,
} from "../hooks/use-magic.ts";
import { useUrlModal } from "../hooks/use-url-dialog";
import type {
  MagicDefinition,
  MagicDefinitionCreateInput,
  MagicDefinitionUpdateInput,
  MagicEvalOutput,
} from "../types/magic.ts";

const MagicPage: React.FC = () => {
  const queryClient = useQueryClient();
  const { data: definitions, isLoading, error } = useMagicDefinitions();
  const [selectedDefinition, setSelectedDefinition] = useState<MagicDefinition | null>(null);
  const [selectedEvalOutput, setSelectedEvalOutput] = useState<MagicEvalOutput | null>(null);
  const [selectedFromEval, setSelectedFromEval] = useState(false);

  // URL-based modal state (fixes back button behavior)
  const [dialogOpen, setDialogOpen] = useUrlModal("edit");
  const [deleteDialogOpen, setDeleteDialogOpen] = useUrlModal("delete");

  const [editingDefinition, setEditingDefinition] = useState<MagicDefinition | null>(null);
  const [menuAnchorEl, setMenuAnchorEl] = useState<null | HTMLElement>(null);

  const createMutation = useCreateMagicDefinition();
  const updateMutation = useUpdateMagicDefinition(editingDefinition?.id || "");
  const deleteMutation = useDeleteMagicDefinition(selectedDefinition?.id || "");

  const handleNodeClick = (definition: MagicDefinition) => {
    setSelectedDefinition(definition);
    setSelectedFromEval(false);
    setSelectedEvalOutput(null);
  };

  const handlePaneClick = () => {
    setSelectedDefinition(null);
    setSelectedFromEval(false);
    setSelectedEvalOutput(null);
  };

  const handleSelectDefinitionByName = (name: string, output?: MagicEvalOutput) => {
    const definition = definitions?.find((def) => def.name === name);
    if (definition) {
      setSelectedDefinition(definition);
      setSelectedEvalOutput(output || null);
      setSelectedFromEval(!!output); // Mark as from eval if output provided
    }
  };

  const handleCreateClick = () => {
    setEditingDefinition(null);
    setDialogOpen(true);
  };

  const handleEditClick = () => {
    if (selectedDefinition && selectedDefinition.source === "manual") {
      setEditingDefinition(selectedDefinition);
      setDialogOpen(true);
      setMenuAnchorEl(null);
    }
  };

  const handleDeleteClick = () => {
    setDeleteDialogOpen(true);
    setMenuAnchorEl(null);
  };

  const handleDeleteConfirm = async () => {
    if (selectedDefinition && selectedDefinition.source === "manual") {
      await deleteMutation.mutateAsync(undefined as never);
      queryClient.invalidateQueries({ queryKey: ["/x/definitions"] });
      setSelectedDefinition(null);
      setDeleteDialogOpen(false);
    }
  };

  const handleDeleteCancel = () => {
    setDeleteDialogOpen(false);
  };

  const handleSaveDefinition = async (
    data: MagicDefinitionCreateInput | MagicDefinitionUpdateInput,
    upstreamChanges?: { addTo: string[]; removeFrom: string[] },
  ) => {
    try {
      // Include upstream_changes in the payload
      const payload = {
        ...data,
        upstream_changes: upstreamChanges
          ? {
              add_to: upstreamChanges.addTo,
              remove_from: upstreamChanges.removeFrom,
            }
          : undefined,
      };

      if (editingDefinition) {
        await updateMutation.mutateAsync(payload as MagicDefinitionUpdateInput);
      } else {
        await createMutation.mutateAsync(payload as MagicDefinitionCreateInput);
      }
      queryClient.invalidateQueries({ queryKey: ["/x/definitions"] });
      setDialogOpen(false);
      setEditingDefinition(null);
    } catch (error) {
      console.error("Failed to save definition:", error);
    }
  };

  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setMenuAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setMenuAnchorEl(null);
  };

  if (isLoading) {
    return (
      <Box data-testid="magic-page">
        <Stack direction="row" spacing={2} alignItems="center" justifyContent="center">
          <CircularProgress size={16} />
          <Typography ml={2}>Loading magic definitions...</Typography>
        </Stack>
      </Box>
    );
  }

  if (error) {
    return (
      <Box data-testid="magic-page">
        <Alert severity="error">
          Could not load magic definitions: {error.message || "Unknown error"}
        </Alert>
      </Box>
    );
  }

  if (!definitions || definitions.length === 0) {
    return (
      <Box data-testid="magic-page">
        <PageHeader
          title="Magic Definitions"
          subtitle="Visual representation of magic definitions and their dependencies"
        />
        <EmptyState variant="alert" message="No magic definitions available." />
      </Box>
    );
  }

  return (
    <Box
      data-testid="magic-page"
      sx={{
        position: "absolute",
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        display: "flex",
        flexDirection: "column",
        overflow: "hidden",
        p: 2,
      }}
    >
      <Box sx={{ flexShrink: 0 }}>
        <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
          <PageHeader
            title="Magic Definitions"
            subtitle="Visual representation of magic definitions and their dependencies. Click on a node to view events and evaluate."
          />
          <Stack direction="row" spacing={1}>
            <Button variant="contained" startIcon={<AddIcon />} onClick={handleCreateClick}>
              Create Definition
            </Button>
            {selectedDefinition && selectedDefinition.source === "manual" && (
              <IconButton onClick={handleMenuOpen}>
                <MoreVertIcon />
              </IconButton>
            )}
          </Stack>
        </Stack>
        <Menu anchorEl={menuAnchorEl} open={Boolean(menuAnchorEl)} onClose={handleMenuClose}>
          <MenuItem onClick={handleEditClick}>
            <EditIcon sx={{ mr: 1 }} fontSize="small" />
            Edit
          </MenuItem>
          <MenuItem onClick={handleDeleteClick} sx={{ color: "error.main" }}>
            <DeleteIcon sx={{ mr: 1 }} fontSize="small" />
            Delete
          </MenuItem>
        </Menu>
      </Box>

      <Box sx={{ flex: 1, overflow: "hidden", minHeight: 0 }}>
        <PanelGroup direction="horizontal">
          <Panel defaultSize={60} minSize={30} maxSize={80}>
            <Box
              sx={{
                height: "100%",
                pr: 1,
                display: "flex",
                flexDirection: "column",
              }}
            >
              <Box sx={{ flex: 1, minHeight: 0 }}>
                <MagicGraphVisualization
                  definitions={definitions}
                  selectedDefinition={selectedDefinition}
                  selectedEvalOutput={selectedFromEval ? selectedEvalOutput : null}
                  onNodeClick={handleNodeClick}
                  onPaneClick={handlePaneClick}
                />
              </Box>
            </Box>
          </Panel>
          <PanelResizeHandle>
            <Divider orientation="vertical">
              <MultipleStopIcon color="disabled" fontSize="small" />
            </Divider>
          </PanelResizeHandle>
          <Panel defaultSize={40} minSize={20} maxSize={70}>
            <Box
              sx={{
                height: "100%",
                backgroundColor: "background.paper",
                overflow: "hidden",
              }}
            >
              <MagicSidePanel
                selectedDefinition={selectedDefinition}
                onSelectDefinitionByName={handleSelectDefinitionByName}
                selectedEvalOutput={selectedEvalOutput}
              />
            </Box>
          </Panel>
        </PanelGroup>
      </Box>

      <MagicDefinitionDialog
        open={dialogOpen}
        onClose={() => {
          setDialogOpen(false);
          setEditingDefinition(null);
        }}
        onSave={handleSaveDefinition}
        definition={editingDefinition}
        existingDefinitions={definitions || []}
      />

      <Dialog open={deleteDialogOpen} onClose={handleDeleteCancel} maxWidth="sm" fullWidth>
        <DialogTitle>Delete Definition</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to delete the definition "{selectedDefinition?.name}" (
            {selectedDefinition?.x_id})?
            <br />
            <br />
            This action cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDeleteCancel}>Cancel</Button>
          <Button
            onClick={handleDeleteConfirm}
            color="error"
            variant="contained"
            disabled={deleteMutation.isPending}
          >
            {deleteMutation.isPending ? "Deleting..." : "Delete"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default MagicPage;
